<template>
  <div class="visualization-content pa-4">
    <v-card outlined elevation="2" class="fact-chart-panel">
      <v-card-title class="visualization-title">
        <v-icon left color="primary">mdi-chart-line</v-icon>
        TCO Graph
        <v-spacer></v-spacer>
        <v-progress-circular
          v-if="isLoading"
          indeterminate
          size="22"
          width="2"
          color="primary"
        ></v-progress-circular>
      </v-card-title>
      <v-card-text>
        <div class="tco-controls">
          <div class="fact-chart-control-block">
            <div class="fact-chart-control-label">Vehicle source</div>
            <v-btn-toggle v-model="tcoMode" mandatory dense color="primary" class="fact-chart-toggle">
              <v-btn value="domestic" small>Domestic</v-btn>
              <v-btn value="imported" small>Imported</v-btn>
            </v-btn-toggle>
          </div>
          <div class="fact-chart-control-block tco-vehicle-control">
            <div class="fact-chart-control-label">Vehicle type</div>
            <v-btn-toggle v-model="tcoVehicle" mandatory dense color="primary" class="tco-vehicle-toggle">
              <v-btn v-for="vehicle in tcoVehicleOptions" :key="vehicle.value" :value="vehicle.value" small>
                {{ vehicle.text }}
              </v-btn>
            </v-btn-toggle>
          </div>
        </div>

        <v-alert v-if="error" type="warning" outlined dense class="mb-4">
          {{ error }}
        </v-alert>

        <div class="tco-chart-layout">
          <div class="fact-price-panel">
            <div class="fact-price-panel-header">
              <div>
                <div class="chart-title-line">
                  <h4>{{ formatChartTitle(activeTcoPerVehicleSeries, 'Total Cost of Ownership per Vehicle') }}</h4>
                  <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeTcoPerVehicleSeries.source_range" />
                </div>
              </div>
            </div>
            <div class="fact-price-canvas-wrap">
              <div v-if="!activeTcoPerVehicleSeries.datasets.length" class="chart-empty-state">
                No TCO data available for this selection.
              </div>
              <canvas ref="tcoPerVehicleChart"></canvas>
            </div>
          </div>
          <div class="fact-price-panel">
            <div class="fact-price-panel-header">
              <div>
                <div class="chart-title-line">
                  <h4>{{ formatChartTitle(activeTcoPerVkmSeries, 'Total Cost of Ownership per VKM') }}</h4>
                  <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeTcoPerVkmSeries.source_range" />
                </div>
              </div>
            </div>
            <div class="fact-price-canvas-wrap">
              <div v-if="!activeTcoPerVkmSeries.datasets.length" class="chart-empty-state">
                No TCO data available for this selection.
              </div>
              <canvas ref="tcoPerVkmChart"></canvas>
            </div>
          </div>
        </div>

        <v-divider class="my-6"></v-divider>

        <div class="fact-stock-section">
          <div class="fact-stock-header">
            <div>
              <div class="chart-title-line">
                <h3>{{ formatChartTitle(activeTcoSummarySeries, 'Total Cost of Ownership Summary') }}</h3>
                <source-tooltip-icon :enabled="showParameterSourceTooltips" :text="activeTcoSummarySeries.source_range" />
              </div>
            </div>
              <div class="tco-summary-controls">
                <v-btn-toggle v-model="tcoSummaryMode" mandatory dense color="primary" class="fact-chart-toggle">
                  <v-btn value="domestic" small>Domestic</v-btn>
                  <v-btn value="imported" small>Imported</v-btn>
                </v-btn-toggle>
                <v-btn-toggle v-model="tcoSummaryYear" mandatory dense color="primary" class="fact-chart-toggle">
                  <v-btn v-for="option in tcoSummaryYearOptions" :key="option.value" :value="option.value" small>
                    {{ option.text }}
                  </v-btn>
                </v-btn-toggle>
              </div>
            </div>
          <div class="tco-summary-canvas-wrap">
            <div v-if="!activeTcoSummarySeries.datasets.length" class="chart-empty-state">
              No TCO summary data available for this selection.
            </div>
            <canvas ref="tcoSummaryChart"></canvas>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import ChartJS from './chartSetup'
import { getTcoCostColor } from './colorPalette'
import SourceTooltipIcon from './SourceTooltipIcon.vue'
import { SHOW_PARAMETER_SOURCE_TOOLTIPS } from './sourceTooltipConfig'

export default {
  name: 'TcoGraphTab',
  components: {
    SourceTooltipIcon
  },
  props: {
    domain: {
      type: String,
      required: true
    },
    analysisResult: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      tcoGraphData: null,
      error: '',
      isLoading: false,
      tcoMode: 'domestic',
      tcoVehicle: 'car',
      tcoSummaryMode: 'domestic',
      tcoSummaryYear: '2024',
      tcoPerVehicleChartInstance: null,
      tcoPerVkmChartInstance: null,
      tcoSummaryChartInstance: null
    }
  },
    computed: {
      showParameterSourceTooltips() {
        return SHOW_PARAMETER_SOURCE_TOOLTIPS
      },
      tcoSummaryYearOptions() {
        if (this.tcoGraphData && Array.isArray(this.tcoGraphData.summary_years) && this.tcoGraphData.summary_years.length) {
          return this.tcoGraphData.summary_years.map((year) => ({
            text: String(year),
            value: String(year)
          }))
        }

        return [
          { text: '2024', value: '2024' },
          { text: '2030', value: '2030' },
          { text: '2035', value: '2035' }
        ]
      },
      tcoVehicleOptions() {
        if (this.tcoGraphData && this.tcoGraphData.vehicles) {
          return this.tcoGraphData.vehicles
        }

      return [
        { text: 'Car', value: 'car' },
        { text: '2 Wheeler', value: '2w' },
        { text: '3 Wheeler', value: '3w' },
        { text: 'Bus', value: 'bus' },
        { text: 'LCV', value: 'lcv' },
        { text: 'HDV', value: 'hdv' }
      ]
    },
    activeTcoPerVehicleSeries() {
      return this.getActiveTcoSeries('per_vehicle')
    },
    activeTcoPerVkmSeries() {
      return this.getActiveTcoSeries('per_vkm')
    },
    activeTcoSummarySeries() {
      const emptySeries = { title: '', subtitle: '', unit: '', categories: [], datasets: [] }
      if (!this.tcoGraphData || !this.tcoGraphData.summaries) return emptySeries

      const modeData = this.tcoGraphData.summaries[this.tcoSummaryMode]
      if (!modeData) return emptySeries

      return modeData[this.tcoSummaryYear] || emptySeries
    }
  },
  watch: {
    analysisResult() {
      this.fetchTcoGraphData()
    },
    tcoMode() {
      this.$nextTick(this.renderTcoCharts)
    },
    tcoVehicle() {
      this.$nextTick(this.renderTcoCharts)
    },
    tcoSummaryMode() {
      this.$nextTick(this.renderTcoSummaryChart)
    },
    tcoSummaryYear() {
      this.$nextTick(this.renderTcoSummaryChart)
    }
  },
  mounted() {
    this.fetchTcoGraphData()
  },
  beforeDestroy() {
    this.destroyTcoCharts()
  },
  methods: {
    formatChartTitle(series, fallback) {
      const title = (series && series.title) || fallback
      const axisTitle = series && (series.y_axis_title || series.unit)
      if (!axisTitle || title.toLowerCase().includes(String(axisTitle).toLowerCase())) {
        return title
      }
      return `${title} (${axisTitle})`
    },
      getPreloadedTcoGraphData() {
        const data = this.analysisResult && this.analysisResult.preloaded_outputs
          ? this.analysisResult.preloaded_outputs.tco_graph || null
          : null
        return this.hasUsablePreloadedTcoGraphData(data) ? data : null
      },
      hasUsablePreloadedTcoGraphData(data) {
        if (!data || !data.summaries) return false
        const hasSummary = Object.values(data.summaries).some((modeData) =>
          modeData && Object.values(modeData).some((series) => series && Array.isArray(series.datasets) && series.datasets.length)
        )
        const hasCharts = data.charts && Object.values(data.charts).some((modeData) =>
          modeData && Object.values(modeData).some((vehicleData) =>
            vehicleData && Object.values(vehicleData).some((series) => series && Array.isArray(series.datasets) && series.datasets.length)
          )
        )
        return !!(hasSummary || hasCharts)
      },
    async fetchTcoGraphData() {
      const requestedFileName = this.analysisResult ? this.analysisResult.file_name : null
      const preloadedData = this.getPreloadedTcoGraphData()
      try {
        this.isLoading = true
        this.error = ''
        if (preloadedData) {
          console.log('[Output Read] TCO Graph:', preloadedData.file_name || requestedFileName || 'preloaded data')
          this.$emit('file-read', {
            section: 'tco_graph',
            sectionTitle: 'TCO Graph',
            fileName: preloadedData.file_name || requestedFileName
            })
            this.tcoGraphData = preloadedData
            this.syncTcoSelections()
            this.$nextTick(() => {
              this.renderTcoCharts()
              this.renderTcoSummaryChart()
            })
            return
        }

        this.$emit('loading-state', {
          section: 'tco_graph',
          sectionTitle: 'TCO Graph',
          fileName: requestedFileName,
          isLoading: true
        })

        const response = await fetch(this.domain + '/zev/tco_graph_data', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            file_name: requestedFileName
          })
        })

        const data = await response.json()
        if (!response.ok) {
          throw new Error(data.detail || data.error || `HTTP error! status: ${response.status}`)
        }
        if (data.error) throw new Error(data.error)

          this.tcoGraphData = data
          this.syncTcoSelections()
          console.log('[Output Read] TCO Graph:', data.file_name || requestedFileName || 'latest analysis workbook')
          this.$emit('file-read', {
            section: 'tco_graph',
            sectionTitle: 'TCO Graph',
            fileName: data.file_name || requestedFileName
        })
        this.$nextTick(() => {
          this.renderTcoCharts()
          this.renderTcoSummaryChart()
        })
      } catch (error) {
        console.error('Error loading TCO Graph data:', error)
        this.error = error.message
        this.tcoGraphData = null
        this.destroyTcoCharts()
      } finally {
        this.isLoading = false
        this.$emit('loading-state', {
          section: 'tco_graph',
          sectionTitle: 'TCO Graph',
          fileName: requestedFileName,
          isLoading: false
          })
        }
      },
      syncTcoSelections() {
        const yearValues = this.tcoSummaryYearOptions.map((option) => option.value)
        if (yearValues.length && !yearValues.includes(this.tcoSummaryYear)) {
          this.tcoSummaryYear = yearValues[0]
        }
      },
      getActiveTcoSeries(metric) {
        const emptySeries = { title: '', subtitle: '', unit: '', categories: [], datasets: [] }
        if (!this.tcoGraphData || !this.tcoGraphData.charts) return emptySeries

      const modeData = this.tcoGraphData.charts[this.tcoMode]
      if (!modeData) return emptySeries

      const vehicleData = modeData[this.tcoVehicle]
      if (!vehicleData) return emptySeries

      return vehicleData[metric] || emptySeries
    },
    destroyTcoCharts() {
      this.tcoPerVehicleChartInstance = this.destroyChart(this.tcoPerVehicleChartInstance)
      this.tcoPerVkmChartInstance = this.destroyChart(this.tcoPerVkmChartInstance)
      this.tcoSummaryChartInstance = this.destroyChart(this.tcoSummaryChartInstance)
    },
    destroyChart(chart) {
      if (chart) chart.destroy()
      return null
    },
    destroyChartOnCanvas(canvas) {
      if (!canvas || !ChartJS.getChart) return
      const existingChart = ChartJS.getChart(canvas)
      if (existingChart) existingChart.destroy()
    },
    renderTcoCharts() {
      this.tcoPerVehicleChartInstance = this.renderTcoStackedBarChart(
        this.$refs.tcoPerVehicleChart,
        this.tcoPerVehicleChartInstance,
        this.activeTcoPerVehicleSeries
      )
      this.tcoPerVkmChartInstance = this.renderTcoStackedBarChart(
        this.$refs.tcoPerVkmChart,
        this.tcoPerVkmChartInstance,
        this.activeTcoPerVkmSeries
      )
    },
    renderTcoSummaryChart() {
      this.tcoSummaryChartInstance = this.renderTcoStackedBarChart(
        this.$refs.tcoSummaryChart,
        this.tcoSummaryChartInstance,
        this.activeTcoSummarySeries
      )
    },
    renderTcoStackedBarChart(canvas, chartInstance, series) {
      if (!canvas) return chartInstance

      if (chartInstance) chartInstance.destroy()
      this.destroyChartOnCanvas(canvas)

      const datasets = series.datasets || []
      if (!datasets.length) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        return null
      }

      const formatTotal = (value) => {
        if (series.unit === '$/vkm') {
          return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        }
        return Math.round(Number(value)).toLocaleString()
      }

      const totalLabelPlugin = {
        id: `tcoTotalLabels-${Math.random().toString(36).slice(2)}`,
        afterDatasetsDraw: (chart) => {
          const { ctx, scales } = chart
          const yScale = scales.y
          const xScale = scales.x
          if (!ctx || !yScale || !xScale) return

          ctx.save()
          ctx.fillStyle = '#424242'
          ctx.font = '600 11px Arial'
          ctx.textAlign = 'center'
          ctx.textBaseline = 'bottom'

          chart.data.labels.forEach((label, index) => {
            const total = chart.data.datasets.reduce((sum, dataset, datasetIndex) => {
              if (!chart.isDatasetVisible(datasetIndex)) return sum
              return sum + Number(dataset.data[index] || 0)
            }, 0)
            if (total <= 0) return
            ctx.fillText(formatTotal(total), xScale.getPixelForValue(label, index), yScale.getPixelForValue(total) - 4)
          })

          ctx.restore()
        }
      }

      return new ChartJS(canvas.getContext('2d'), {
        type: 'bar',
        data: {
          labels: series.categories,
          datasets: datasets.map(dataset => ({
            label: dataset.label,
            data: dataset.values,
            backgroundColor: getTcoCostColor(dataset.label),
            borderColor: '#ffffff',
            borderWidth: 1
          }))
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: { padding: { top: 18 } },
          interaction: { mode: 'index', intersect: false },
          plugins: {
            legend: { position: 'bottom' },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const value = Number(context.parsed.y)
                  const decimals = series.unit === '$/vkm' ? 3 : 0
                  return `${context.dataset.label}: ${value.toLocaleString(undefined, {
                    minimumFractionDigits: decimals,
                    maximumFractionDigits: decimals
                  })}`
                }
              }
            }
          },
          scales: {
            x: { stacked: true, grid: { display: false } },
            y: {
              stacked: true,
              beginAtZero: true,
              grace: '12%',
              ticks: { callback: (value) => Number(value).toLocaleString() }
            }
          }
        },
        plugins: [totalLabelPlugin]
      })
    }
  }
}
</script>
